import React from 'react';
import { Edit, Trash2, Eye, MessageCircle } from 'lucide-react';
import styles from './PortfolioItemCard.module.scss';
import '@github/relative-time-element';
import ProductIcon from '../../../../assets/iconsTsx/ProductIcon.tsx';
import ServiceIcon from '../../../../assets/iconsTsx/ServiceIcon.tsx';

interface PortfolioItemCardProps {
  portfolio: {
    id: string | number;
    name: string;
    description?: string;
    type?: 'producto' | 'servicio';
    price?: string | number;
    priceUnit?: string;
    createdAt?: string;
    portfolio_images?: Array<{ image: string }>;
  };
  myPerson: boolean;
  onEdit?: (id: string | number) => void;
  onDelete?: (id: string | number) => void;
  onView?: (id: string | number) => void;
  onOpenQuestions?: (id: string | number) => void;
  questionsNotAnswered?: number;
}

const PortfolioItemCard: React.FC<PortfolioItemCardProps> = ({
  portfolio,
  myPerson,
  onEdit,
  onDelete,
  onView,
  onOpenQuestions,
  questionsNotAnswered = 0
}) => {
  const coverImage = portfolio?.portfolio_images?.[0]?.image || null;

  const formatPrice = (price: string | number): string => {
    if (!price) return '';
    const numPrice = typeof price === 'string' ? parseInt(price, 10) : price;
    return `$ ${new Intl.NumberFormat('es-AR').format(numPrice)}`;
  };

  return (
    <div className={styles.card}>
      {coverImage && (
        <div className={styles.card_image}>
          <img 
            src={coverImage} 
            alt={portfolio.name}
            className={styles.card_image_img}
          />
        </div>
      )}

      <div className={styles.card_header}>
        <div className={styles.card_header_left}>
          <h3 className={styles.card_title}>{portfolio.name}</h3>
          {portfolio.type && (
            <span className={styles.card_badge}>
              {portfolio.type === 'producto' ? (
                <ProductIcon className={styles.card_badge_icon} />
              ) : (
                <ServiceIcon className={styles.card_badge_icon} />
              )}
              <span className={styles.card_badge_text}>
                {portfolio.type === 'producto' ? 'Producto' : 'Servicio'}
              </span>
            </span>
          )}
        </div>
        {myPerson && onEdit && (
          <div className={styles.card_header_actions}>
            {onEdit && (
              <button
                className={styles.actionButton}
                onClick={() => onEdit(portfolio.id)}
                title="Editar"
              >
                <Edit size={16} />
              </button>
            )}
            {onDelete && (
              <button
                className={`${styles.actionButton} ${styles.actionButton_delete}`}
                onClick={() => onDelete(portfolio.id)}
                title="Eliminar"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.card_body}>
        <div className={styles.card_info}>
          <p className={styles.card_description}>
            {portfolio.description || 'Sin descripción'}
          </p>

          <div className={styles.card_details}>
            {portfolio.price && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Precio:</span>
                <span className={styles.detailValue}>
                  {formatPrice(portfolio.price)}
                  {portfolio.priceUnit && ` x ${portfolio.priceUnit}`}
                </span>
              </div>
            )}

            {portfolio.createdAt && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Creado:</span>
                <span className={styles.detailValue}>
                  <relative-time lang="es" tense="past" datetime={portfolio.createdAt}></relative-time>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.card_footer}>
        {myPerson && onOpenQuestions && questionsNotAnswered > 0 && (
          <button
            className={styles.questionsButton}
            onClick={() => onOpenQuestions(portfolio.id)}
          >
            <MessageCircle size={16} />
            Responder preguntas
            <span className={styles.questionsButton_badge}>{questionsNotAnswered}</span>
          </button>
        )}
        {onView && (
          <button
            className={styles.viewButton}
            onClick={() => onView(portfolio.id)}
          >
            <Eye size={16} />
            Ver detalles
          </button>
        )}
      </div>
    </div>
  );
};

export default PortfolioItemCard;

